const SITE_URL = "https://valleysunscreens.com";

export const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "HomeAndConstructionBusiness",
  name: "Valley Sun Screens",
  description:
    "Phoenix's most affordable sun screen installer. Top-quality custom solar screens at the lowest prices — block heat, lower energy bills, and protect your home.",
  url: SITE_URL,
  telephone: "+1-623-301-4595",
  email: "info@valleysunscreens.com",
  image: `${SITE_URL}/og-image.jpg`,
  logo: `${SITE_URL}/favicon.png`,
  priceRange: "$$",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Phoenix",
    addressRegion: "AZ",
    addressCountry: "US",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 33.4484,
    longitude: -112.074,
  },
  areaServed: [
    "Surprise", "Glendale", "Peoria", "Goodyear", "Buckeye",
    "Avondale", "Sun City", "El Mirage", "Waddell", "Wittmann",
  ].map((city) => ({
    "@type": "City",
    name: `${city}, AZ`,
  })),
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
    opens: "07:00",
    closes: "18:00",
  },
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "5.0",
    reviewCount: "12",
    bestRating: "5",
    worstRating: "1",
  },
  sameAs: [
    "https://maps.app.goo.gl/kRXUJi8YucuitEr88",
  ],
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Sun Screen Services",
    itemListElement: [
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Sun Screen Installation",
          description:
            "Custom solar screen installation for residential windows. Blocks solar heat.",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Screen Repair & Replacement",
          description:
            "Repair and replacement of damaged or worn sun screens with professional installation.",
        },
      },
    ],
  },
};

/**
 * Builds a LocalBusiness schema with live AggregateRating values pulled from
 * the google-reviews edge function. Falls back to static defaults when live
 * data is unavailable so search snippets never lose their stars.
 */
export const buildLocalBusinessSchema = (live?: {
  rating?: number;
  totalReviews?: number;
}) => {
  const rating = live?.rating && live.rating > 0 ? live.rating : 5.0;
  const count =
    live?.totalReviews && live.totalReviews > 0 ? live.totalReviews : 12;
  return {
    ...localBusinessSchema,
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: rating.toFixed(1),
      reviewCount: String(count),
      bestRating: "5",
      worstRating: "1",
    },
  };
};

export const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "How much do sun screens cost?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "On average, screens cost $100–$120 each but can vary depending on size. That price includes our full service — measuring, custom building, and professional installation.",
      },
    },
    {
      "@type": "Question",
      name: "How long does installation take?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Most homes are completed in a single day. Larger projects may take 1–2 days depending on the number of windows.",
      },
    },
    {
      "@type": "Question",
      name: "Do sun screens block the view?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Not at all! Our screens reduce glare and heat while allowing you to see outside clearly. They look like a light tint from inside.",
      },
    },
    {
      "@type": "Question",
      name: "What areas do you serve?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "We proudly serve the West Valley including Surprise, Glendale, Peoria, Goodyear, Buckeye, Avondale, Sun City, El Mirage, Waddell, and Wittmann.",
      },
    },
    {
      "@type": "Question",
      name: "How long do sun screens last?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Our premium sun screens typically last around 7–10 years, though lifespan can vary depending on sun exposure, weather, and how the screens are cared for.",
      },
    },
    {
      "@type": "Question",
      name: "Are sun screens worth it in Arizona?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Absolutely! Arizona's intense sun makes sun screens one of the best home investments. Most homeowners notice lower energy bills in the summer months.",
      },
    },
    {
      "@type": "Question",
      name: "Are deposits refundable?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Deposits are non-refundable because they cover the cost of materials and labor required to custom-build your sun screens. Each screen is made to the exact measurements of your windows, so if a project is canceled after production begins, we are left with a product that cannot be used for another home.",
      },
    },
  ],
};

export const breadcrumbSchema = (items: { name: string; url: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: item.name,
    item: `${SITE_URL}${item.url}`,
  })),
});

export const serviceAreaSchema = (cityName: string, slug: string) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name: `Sun Screen Installation in ${cityName}, AZ`,
  description: `Professional solar screen installation for ${cityName} homeowners. Block the sun's heat and save on energy bills.`,
  url: `${SITE_URL}/areas/${slug}`,
  provider: {
    "@type": "HomeAndConstructionBusiness",
    name: "Valley Sun Screens",
    telephone: "+1-623-301-4595",
  },
  areaServed: {
    "@type": "City",
    name: `${cityName}, AZ`,
  },
  serviceType: "Sun Screen Installation",
});
